import React, { useState, useEffect } from 'react';
import './CharacterLibrary.css'; /* Reuses library styles */
import { influencerService, getInfluencerImageUrl } from '../services/influencerApi';

function EnvironmentLibrary({ onNavigate }) {
  const [environments, setEnvironments] = useState([]);
  const [filteredEnvironments, setFilteredEnvironments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEnvironment, setSelectedEnvironment] = useState(null);

  useEffect(() => {
    loadEnvironments();
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredEnvironments(environments);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = environments.filter((env) =>
        env.filename.toLowerCase().includes(query) ||
        (env.name && env.name.toLowerCase().includes(query))
      );
      setFilteredEnvironments(filtered);
    }
  }, [searchQuery, environments]);

  const loadEnvironments = async () => {
    try {
      setIsLoading(true);
      const response = await influencerService.getBackgroundLibrary();
      if (response.success && response.backgrounds) {
        setEnvironments(response.backgrounds);
        setFilteredEnvironments(response.backgrounds);
      }
    } catch (error) {
      console.error('Failed to load environments:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="library-page">
      <div className="header">
        <h1>Environment Library</h1>
        <p className="subtitle">Browse and manage your AI-generated environments</p>
      </div>

      {/* Search & Stats */}
      <div className="library-toolbar">
        <div className="search-box">
          <i className="fas fa-search"></i>
          <input
            type="text"
            placeholder="Search by name or filename..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="library-stats">
          <i className="fas fa-image"></i>
          <span>{filteredEnvironments.length} environment{filteredEnvironments.length !== 1 ? 's' : ''}</span>
        </div>
        <button className="xbtn xbtn-md xbtn-secondary" onClick={loadEnvironments}>
          <i className={`fas fa-sync-alt ${isLoading ? 'fa-spin' : ''}`}></i>
          Refresh
        </button>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="library-empty">
          <i className="fas fa-spinner fa-spin fa-3x"></i>
          <p>Loading your environments...</p>
        </div>
      )}

      {/* Empty */}
      {!isLoading && filteredEnvironments.length === 0 && (
        <div className="library-empty">
          <i className="fas fa-mountain fa-3x"></i>
          <p>{searchQuery ? 'No environments found' : 'No environments yet'}</p>
          <small>{searchQuery ? 'Try a different search term' : 'Generate your first environment to get started'}</small>
        </div>
      )}

      {/* Grid */}
      {!isLoading && filteredEnvironments.length > 0 && (
        <div className="library-grid env-grid">
          {filteredEnvironments.map((environment) => (
            <div
              key={environment.filename}
              className="library-card"
              onClick={() => setSelectedEnvironment(environment)}
            >
              <div className="library-card-image env-card-image">
                <img
                  src={getInfluencerImageUrl(environment.path)}
                  alt={environment.name || environment.filename}
                  loading="lazy"
                />
                <div className="library-card-overlay">
                  <button
                    className="xbtn xbtn-sm xbtn-secondary card-action-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onNavigate) onNavigate('environment-edit', {
                        path: environment.path,
                        name: environment.name || 'Unnamed',
                        filename: environment.filename,
                      });
                    }}
                  >
                    <i className="fas fa-edit"></i> Edit
                  </button>
                  <button
                    className="xbtn xbtn-sm xbtn-secondary card-action-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedEnvironment(environment);
                    }}
                  >
                    View
                  </button>
                </div>
              </div>
              <div className="library-card-info">
                <h3>{environment.name || 'Unnamed'}</h3>
                <div className="card-meta">
                  <i className="fas fa-calendar"></i>
                  <span>{formatDate(environment.created)}</span>
                </div>
                <div className="card-meta">{formatFileSize(environment.size)}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      {selectedEnvironment && (
        <div className="modal-overlay" onClick={() => setSelectedEnvironment(null)}>
          <div className="detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="detail-modal-header">
              <div>
                <h2>{selectedEnvironment.name || 'Unnamed Environment'}</h2>
                <p className="modal-filename">{selectedEnvironment.filename}</p>
              </div>
              <button className="xbtn xbtn-md xbtn-secondary" onClick={() => setSelectedEnvironment(null)}>Close</button>
            </div>
            <div className="detail-modal-body env-detail-body">
              <div className="detail-image env-detail-image">
                <img
                  src={getInfluencerImageUrl(selectedEnvironment.path)}
                  alt={selectedEnvironment.name || selectedEnvironment.filename}
                />
              </div>
              <div className="detail-info">
                <h3 className="detail-section-title">Details</h3>
                <div className="detail-row"><span>Name:</span><strong>{selectedEnvironment.name || 'Unnamed'}</strong></div>
                <div className="detail-row"><span>Created:</span><strong>{formatDate(selectedEnvironment.created)}</strong></div>
                <div className="detail-row"><span>File Size:</span><strong>{formatFileSize(selectedEnvironment.size)}</strong></div>
                <div className="detail-row"><span>Filename:</span><code>{selectedEnvironment.filename}</code></div>

                <h3 className="detail-section-title" style={{ marginTop: 20 }}>Actions</h3>
                <div className="detail-actions">
                  <button
                    className="xbtn xbtn-md xbtn-primary"
                    onClick={() => {
                      if (onNavigate) onNavigate('environment-edit', {
                        path: selectedEnvironment.path,
                        name: selectedEnvironment.name || 'Unnamed',
                        filename: selectedEnvironment.filename,
                      });
                    }}
                  >
                    <i className="fas fa-edit"></i> Edit Environment
                  </button>
                  <button className="xbtn xbtn-md xbtn-secondary" onClick={() => window.open(getInfluencerImageUrl(selectedEnvironment.path), '_blank')}>
                    Open Full Size
                  </button>
                  <button className="xbtn xbtn-md xbtn-secondary" onClick={() => {
                    const link = document.createElement('a');
                    link.href = getInfluencerImageUrl(selectedEnvironment.path);
                    link.download = selectedEnvironment.filename;
                    link.click();
                  }}>
                    Download
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EnvironmentLibrary;
